<script lang="ts">
  import { Duration } from 'luxon'
  import { ceil } from '../../../helpers/math'
  import {
    computeStatsCumulatives,
    formatPrintingTime,
    getFreshMortarMassKg,
    getGcodeLayerHeight,
    getLiftSpeedCmPerH,
    getMaterialLengthM,
    getMaterialQuantityKg,
    getPrintingTimeSeconds,
    getPrintVolumeM3,
    getWaterQuantityL,
  } from '../../../helpers/gcode-stats'
  import Button from '../../../components/button/Button.svelte'
  import {
    Box,
    ChartPie,
    Clock,
    Coins,
    Droplet,
    MoveVertical,
    Package,
    Ruler,
    Weight,
    X,
  } from '@lucide/svelte'
  import i18n from '../../../i18n'
  import Tooltip from '../../../components/tooltip/Tooltip.svelte'
  import materials from '../../../constants/materials'
  import Input from '../../../components/input/Input.svelte'
  import Select from '../../../components/select/Select.svelte'
  import StatisticCard from '../../../components/statistic-card/StatisticCard.svelte'
  import StatisticCardPrimary from '../../../components/statistic-card/StatisticCardPrimary.svelte'
  import StatisticCardSecondary from '../../../components/statistic-card/StatisticCardSecondary.svelte'

  type Props = {
    parsedGcode: ParsedGcodeLine[]
    progress: number
  }

  const { parsedGcode, progress }: Props = $props()
  let printingTime = $state<Duration>()
  let materialLengthM = $state<number>()
  let freshMortarVolumeM3 = $state<number>()
  let freshMortarMassKg = $state<number>()
  let dryMaterialMassKg = $state<number>()
  let materialWeightBigBag = $state<number>()
  let waterVolumeL = $state<number>()
  let liftSpeedCmH = $state<number>()
  let hidden = $state(false)
  let layerWidthMm = $state(60)
  let printSpeedMmS = $state(100)
  let priceEurBigBag = $state(0)
  let selectedMaterialId = $state<MaterialStat['id']>(materials[0].id)
  let selectedMaterial = $derived(
    materials.find(material => material.id === selectedMaterialId),
  )

  // Both are recomputed only when the gcode itself changes, not on every frame.
  const statsCumulatives = $derived(computeStatsCumulatives(parsedGcode))
  const layerHeightMm = $derived(getGcodeLayerHeight(parsedGcode))

  $effect(() => {
    if (parsedGcode.length === 0) return

    materialLengthM = getMaterialLengthM(statsCumulatives, progress)
    printingTime = formatPrintingTime(
      getPrintingTimeSeconds(statsCumulatives, progress, printSpeedMmS),
    )
    liftSpeedCmH = getLiftSpeedCmPerH(statsCumulatives, progress, printSpeedMmS)

    const printVolume = getPrintVolumeM3(
      materialLengthM,
      layerHeightMm,
      layerWidthMm,
    )
    freshMortarVolumeM3 = ceil(printVolume, 2)
    freshMortarMassKg = getFreshMortarMassKg(printVolume, selectedMaterial)
    dryMaterialMassKg = getMaterialQuantityKg(printVolume, selectedMaterial)
    materialWeightBigBag = ceil(dryMaterialMassKg / 1000)
    waterVolumeL = getWaterQuantityL(printVolume, selectedMaterial)
  })

  $effect(() => {
    if (selectedMaterial) {
      priceEurBigBag = selectedMaterial.defaultPriceEurBigBag
    }
  })
</script>

<div class="stats-container">
  {#if hidden}
    <Tooltip
      placement="left"
      title={$i18n.t('projectPreview.statistics.title')}
    >
      <button
        class="stats-button"
        onclick={() => (hidden = !hidden)}
      >
        <ChartPie />
      </button>
    </Tooltip>
  {:else}
    <div class="card">
      <div class="card-header">
        {$i18n.t('projectPreview.statistics.title')}
        <Button
          onclick={() => (hidden = !hidden)}
          variant="ghost"
        >
          <X />
        </Button>
      </div>
      <div class="card-body">
        <div>
          <Select
            name="zone"
            options={materials.map(material => ({
              label: material.label,
              value: material.id,
            }))}
            bind:value={selectedMaterialId}
          />
        </div>
        <div>
          <Input
            label={$i18n.t('projectPreview.statistics.form.layerWidth')}
            type="number"
            bind:value={layerWidthMm}
          />
        </div>
        <div>
          <Input
            label={$i18n.t('projectPreview.statistics.form.printSpeed')}
            type="number"
            min={0}
            max={250}
            bind:value={printSpeedMmS}
          />
        </div>
        <div>
          <Input
            label={$i18n.t('projectPreview.statistics.form.priceBigBag')}
            type="number"
            bind:value={priceEurBigBag}
          />
        </div>
        <div class="divider"></div>
        {#snippet massValue(kg: number)}
          {#if kg > 1000}
            <StatisticCardPrimary>{ceil(kg / 1000, 1)}</StatisticCardPrimary>
            <StatisticCardSecondary>
              {$i18n.t('projectPreview.statistics.materialWeight.t')}
            </StatisticCardSecondary>
          {:else}
            <StatisticCardPrimary>{ceil(kg, 1)}</StatisticCardPrimary>
            <StatisticCardSecondary>
              {$i18n.t('projectPreview.statistics.materialWeight.kg')}
            </StatisticCardSecondary>
          {/if}
        {/snippet}
        <div class="stats-grid">
          <div class="column">
            <div class="row">
              {#if printingTime !== undefined}
                <div class="column">
                  <StatisticCard
                    icon={Clock}
                    label={$i18n.t(
                      'projectPreview.statistics.printingTime.label',
                    )}
                  >
                    <StatisticCardPrimary>{printingTime.hours}</StatisticCardPrimary>
                    <StatisticCardSecondary>
                      {$i18n.t('projectPreview.statistics.printingTime.hours')}
                    </StatisticCardSecondary>
                    &nbsp;
                    <StatisticCardPrimary>
                      {printingTime.minutes}
                    </StatisticCardPrimary>
                    <StatisticCardSecondary>
                      {$i18n.t(
                        'projectPreview.statistics.printingTime.minutes',
                      )}
                    </StatisticCardSecondary>
                  </StatisticCard>
                </div>
              {/if}
              {#if materialLengthM !== undefined}
                <div class="column">
                  <StatisticCard
                    icon={Ruler}
                    label={$i18n.t(
                      'projectPreview.statistics.materialLength.label',
                    )}
                  >
                    <StatisticCardPrimary>{materialLengthM}</StatisticCardPrimary>
                    <StatisticCardSecondary>
                      {$i18n.t('projectPreview.statistics.materialLength.m')}
                    </StatisticCardSecondary>
                  </StatisticCard>
                </div>
              {/if}
            </div>
            <div class="row">
              {#if freshMortarVolumeM3 !== undefined}
                <div class="column">
                  <StatisticCard
                    icon={Box}
                    label={$i18n.t(
                      'projectPreview.statistics.freshMortarVolume.label',
                    )}
                  >
                    <StatisticCardPrimary>{freshMortarVolumeM3}</StatisticCardPrimary>
                    <StatisticCardSecondary>
                      {$i18n.t('projectPreview.statistics.freshMortarVolume.m3')}
                    </StatisticCardSecondary>
                  </StatisticCard>
                </div>
              {/if}
              {#if liftSpeedCmH !== undefined}
                <div class="column">
                  <StatisticCard
                    icon={MoveVertical}
                    label={$i18n.t('projectPreview.statistics.liftSpeed.label')}
                  >
                    <StatisticCardPrimary>{liftSpeedCmH}</StatisticCardPrimary>
                    <StatisticCardSecondary>
                      {$i18n.t('projectPreview.statistics.liftSpeed.cmh')}
                    </StatisticCardSecondary>
                  </StatisticCard>
                </div>
              {/if}
            </div>
            <div class="row">
              {#if freshMortarMassKg !== undefined}
                <div class="column">
                  <StatisticCard
                    icon={Weight}
                    label={$i18n.t(
                      'projectPreview.statistics.freshMortarMass.label',
                    )}
                  >
                    <div class="weight-line-container">
                      {@render massValue(freshMortarMassKg)}
                    </div>
                  </StatisticCard>
                </div>
              {/if}
              {#if dryMaterialMassKg !== undefined}
                <div class="column">
                  <StatisticCard
                    icon={Package}
                    label={$i18n.t(
                      'projectPreview.statistics.materialWeight.label',
                    )}
                  >
                    <div class="weight-container">
                      <div class="weight-line-container">
                        {@render massValue(dryMaterialMassKg)}
                      </div>
                      <div class="divider"></div>
                      <div class="weight-line-container">
                        <StatisticCardPrimary>
                          {materialWeightBigBag}
                        </StatisticCardPrimary>
                        &nbsp;
                        <StatisticCardSecondary>
                          {$i18n.t(
                            'projectPreview.statistics.materialVolume.bigbag',
                          )}
                        </StatisticCardSecondary>
                      </div>
                    </div>
                  </StatisticCard>
                </div>
              {/if}
            </div>
            <div class="row">
              {#if waterVolumeL !== undefined}
                <div class="column">
                  <StatisticCard
                    icon={Droplet}
                    label={$i18n.t(
                      'projectPreview.statistics.waterVolume.label',
                    )}
                  >
                    <StatisticCardPrimary>{waterVolumeL}</StatisticCardPrimary>
                    <StatisticCardSecondary>
                      {$i18n.t('projectPreview.statistics.waterVolume.l')}
                    </StatisticCardSecondary>
                  </StatisticCard>
                </div>
              {/if}
              {#if materialWeightBigBag !== undefined && selectedMaterial !== undefined}
                <div class="column">
                  <StatisticCard
                    icon={Coins}
                    label={$i18n.t('projectPreview.statistics.price.label')}
                  >
                    <StatisticCardPrimary>
                      {new Intl.NumberFormat($i18n.language).format(
                        ceil(materialWeightBigBag * priceEurBigBag),
                      )}
                    </StatisticCardPrimary>
                    &nbsp;
                    <StatisticCardSecondary>€</StatisticCardSecondary>
                  </StatisticCard>
                </div>
              {/if}
            </div>
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>

<style lang="scss">
  .stats-container {
    position: absolute;
    top: 1rem;
    right: 1rem;
    z-index: 1;

    .stats-button {
      align-items: center;
      background: var(--background);
      border-radius: 0.25rem;
      border: 0;
      color: var(--foreground);
      cursor: pointer;
      display: flex;
      justify-content: center;
      padding: 0.25rem;
      transition: all 0.3s ease;

      &:hover {
        background: var(--background-muted);
      }
    }

    .card {
      align-items: stretch;
      background: var(--background);
      border-radius: 0.5rem;
      border: 1px solid var(--grid-stroke);
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      min-width: 16rem;

      .card-header {
        align-items: center;
        border-bottom: 1px solid var(--grid-stroke);
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        padding: 0.5rem 0.5rem 0.5rem 1rem;
      }

      .card-body {
        align-items: stretch;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        justify-content: flex-start;
        padding: 0.5rem 1rem;

        > .divider {
          height: 1px;
          background: var(--grid-stroke);
          margin: 1rem 0;
        }

        .stats-grid {
          display: flex;
          align-items: stretch;

          .column {
            align-items: stretch;
            display: flex;
            flex: 1 1 0;
            flex-direction: column;
            gap: 0.5rem;
          }

          .row {
            align-items: stretch;
            display: flex;
            gap: 0.5rem;
            flex-direction: row;
          }
        }

        .weight-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          flex: 1;

          .weight-line-container {
            display: flex;
            flex-direction: row;
          }

          .divider {
            height: 1px;
            width: 100%;
            background: var(--foreground-muted);
          }
        }
        > div {
          align-items: center;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        }
      }
    }
  }
</style>
